export interface VitalsPrintData {
  bloodPressure?: string | null;
  weight?: string | number | null;
  heartRate?: string | number | null;
  oxygenSaturation?: string | number | null;
  bodyTemperature?: string | number | null;
}

/**
 * Checks whether any vital sign has a non-empty, valid value.
 */
export function hasAnyVitalData(vitals?: VitalsPrintData | null): boolean {
  if (!vitals) {
    return false;
  }

  const isPresent = (val: string | number | null | undefined): boolean => {
    if (val === null || val === undefined) return false;
    const str = String(val).trim();
    return str !== '' && str !== '-' && str !== 'null' && str !== 'undefined';
  };

  return (
    isPresent(vitals.bloodPressure) ||
    isPresent(vitals.heartRate) ||
    isPresent(vitals.bodyTemperature) ||
    isPresent(vitals.weight) ||
    isPresent(vitals.oxygenSaturation)
  );
}

/**
 * Builds HTML for vitals to be embedded into print templates.
 * Returns empty string if no vital details were entered.
 */
export function buildVitalsPrintHtml(vitals?: VitalsPrintData | null): string {
  if (!hasAnyVitalData(vitals) || !vitals) {
    return '';
  }

  const parts: string[] = [];

  const cleanString = (val: string | number | null | undefined): string => {
    if (val === null || val === undefined) return '';
    const str = String(val).trim();
    return (str === '-' || str === 'null' || str === 'undefined') ? '' : str;
  };

  const bp = cleanString(vitals.bloodPressure);
  if (bp) {
    const formattedBp = bp.toLowerCase().includes('mmhg') ? bp : `${bp} mmHg`;
    parts.push(
      `<span class="vital-entry"><span class="vital-entry-label">BP:</span> <span class="vital-entry-value">${escapeHtml(formattedBp)}</span></span>`
    );
  }

  const hr = cleanString(vitals.heartRate);
  if (hr) {
    const formattedHr = hr.toLowerCase().includes('bpm') ? hr : `${hr} bpm`;
    parts.push(
      `<span class="vital-entry"><span class="vital-entry-label">Pulse:</span> <span class="vital-entry-value">${escapeHtml(formattedHr)}</span></span>`
    );
  }

  const temp = cleanString(vitals.bodyTemperature);
  if (temp) {
    const formattedTemp = (temp.includes('°') || temp.toLowerCase().includes('f') || temp.toLowerCase().includes('c'))
      ? temp
      : `${temp} °F`;
    parts.push(
      `<span class="vital-entry"><span class="vital-entry-label">Temp:</span> <span class="vital-entry-value">${escapeHtml(formattedTemp)}</span></span>`
    );
  }

  const wt = cleanString(vitals.weight);
  if (wt) {
    const formattedWt = wt.toLowerCase().includes('kg') ? wt : `${wt} kg`;
    parts.push(
      `<span class="vital-entry"><span class="vital-entry-label">Weight:</span> <span class="vital-entry-value">${escapeHtml(formattedWt)}</span></span>`
    );
  }

  const spo2 = cleanString(vitals.oxygenSaturation);
  if (spo2) {
    const formattedSpo2 = spo2.includes('%') ? spo2 : `${spo2}%`;
    parts.push(
      `<span class="vital-entry"><span class="vital-entry-label">SpO2:</span> <span class="vital-entry-value">${escapeHtml(formattedSpo2)}</span></span>`
    );
  }

  if (parts.length === 0) {
    return '';
  }

  return `
    <div class="vitals-row">
      <span class="vitals-title">Vitals:</span>
      ${parts.join('')}
    </div>
  `;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
